import React, { type PropsWithChildren, useCallback } from 'react';
import { type LayoutChangeEvent, StyleSheet, type ViewProps } from 'react-native';
import Animated, {
  type AnimatedProps,
  type SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { GlassView } from '../../../GlassView';
import { useNavbarPressScale } from '../../hooks/useNavbarPressScale';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarLinksGroup = (props: Props) => {
  const {
    activeBubbleItemCount,
    activeTabIndexSV,
    children,
    glassFillOpacity = DEFAULT_GLASS_FILL_OPACITY,
    isLiquidGlassEnabled = false,
    layout,
    style,
  } = props;

  const { handlePressIn, pressScaleAnimatedStyles } = useNavbarPressScale();

  // ─── Shared Values ───────────────────────────────────────────────────

  const contentWidthSV = useSharedValue(0);
  const activeBubbleStretchSV = useSharedValue(0);

  // ─── Derived ─────────────────────────────────────────────────────────

  const hasActiveBubbleItems = activeBubbleItemCount !== undefined && activeBubbleItemCount > 0;
  const shouldRenderActiveBubble =
    isLiquidGlassEnabled && activeTabIndexSV !== undefined && hasActiveBubbleItems;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      contentWidthSV.value = event.nativeEvent.layout.width - LINK_GROUP_PADDING * 2;
    },
    [contentWidthSV],
  );

  // ─── Animated Reactions ──────────────────────────────────────────────

  // NOTE: Selecting another tab stretches the bubble as it sets off and lets
  //       it spring back into shape on arrival, like the system tab bar's
  //       liquid selection.
  useAnimatedReaction(
    () => activeTabIndexSV?.value,
    (activeIndex, previousActiveIndex) => {
      if (previousActiveIndex === null || activeIndex === previousActiveIndex) return;

      activeBubbleStretchSV.value = withSequence(
        withTiming(1, ACTIVE_BUBBLE_STRETCH_TIMING_CONFIG),
        withSpring(0, ACTIVE_BUBBLE_SETTLE_SPRING_CONFIG),
      );
    },
  );

  // ─── Derived Values ──────────────────────────────────────────────────

  /**
   * Every tab item shares the available width equally, so the bubble is sized
   * from the item count and slides with a transform instead of re-measuring
   * each link. A collapsed width (instead of a fade) keeps the native glass
   * effect alive, since glass stops rendering at opacity 0.
   */
  const activeBubbleWidthSV = useDerivedValue(() => {
    const itemCount = activeBubbleItemCount ?? 0;

    if (itemCount === 0 || contentWidthSV.value === 0) return 0;

    return (contentWidthSV.value - LINK_GROUP_GAP * (itemCount - 1)) / itemCount;
  }, [activeBubbleItemCount]);

  const activeBubbleTranslateXSV = useDerivedValue(() => {
    const activeIndex = activeTabIndexSV?.value ?? 0;
    const translateX = activeIndex * (activeBubbleWidthSV.value + LINK_GROUP_GAP);

    return withSpring(translateX, ACTIVE_BUBBLE_TRAVEL_SPRING_CONFIG);
  });

  const activeBubbleScaleXSV = useDerivedValue(
    () => 1 + activeBubbleStretchSV.value * ACTIVE_BUBBLE_STRETCH_X,
  );
  const activeBubbleScaleYSV = useDerivedValue(
    () => 1 + activeBubbleStretchSV.value * ACTIVE_BUBBLE_STRETCH_Y,
  );

  // ─── Animated Styles ─────────────────────────────────────────────────

  const activeBubbleAnimatedStyles = useAnimatedStyle(() => ({
    width: activeBubbleWidthSV.value,
    transform: [
      { translateX: activeBubbleTranslateXSV.value },
      { scaleX: activeBubbleScaleXSV.value },
      { scaleY: activeBubbleScaleYSV.value },
    ],
  }));

  // ─── Styles ──────────────────────────────────────────────────────────

  const linkGroupGlassStyles = useStyle(
    () => ({ backgroundColor: `rgba(255, 255, 255, ${glassFillOpacity})` }),
    [glassFillOpacity],
  );
  const linkGroupStyles = [
    styles.linkGroup,
    isLiquidGlassEnabled ? linkGroupGlassStyles : styles.linkGroupSolid,
    isLiquidGlassEnabled ? pressScaleAnimatedStyles : undefined,
    style,
  ];
  const activeBubbleStyles = [styles.activeBubble, activeBubbleAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      accessibilityRole="tablist"
      layout={layout}
      style={linkGroupStyles}
      onLayout={handleLayout}
      onTouchStart={isLiquidGlassEnabled ? handlePressIn : undefined}
    >
      {isLiquidGlassEnabled ? (
        <GlassView
          tintColor={theme.colors.OPACITY.BLACK.ALMOST_TRANSPARENT}
          glassEffectStyle="regular"
          isInteractive
          style={styles.glass}
        />
      ) : null}

      {shouldRenderActiveBubble ? (
        <AnimatedGlassView
          tintColor={ACTIVE_BUBBLE_TINT_COLOR}
          glassEffectStyle="regular"
          style={activeBubbleStyles}
        />
      ) : null}

      {children}
    </Animated.View>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedGlassView = Animated.createAnimatedComponent(GlassView);

// ─── Constants ───────────────────────────────────────────────────────────────

const LINK_GROUP_PADDING = theme.spacing['1'];
const LINK_GROUP_GAP = theme.spacing['1'];
const DEFAULT_GLASS_FILL_OPACITY = 0.85;

// NOTE: Travel is a soft spring so the bubble overshoots the target a touch.
//       The stretch ramps up quickly as it departs and springs back to rest
//       with a little wobble once it lands.
const ACTIVE_BUBBLE_TRAVEL_SPRING_CONFIG = { damping: 20, stiffness: 121, mass: 1 };
const ACTIVE_BUBBLE_STRETCH_TIMING_CONFIG = { duration: theme.transitions.base * 0.9 };
const ACTIVE_BUBBLE_SETTLE_SPRING_CONFIG = { damping: 12, stiffness: 154, mass: 0.8 };

const ACTIVE_BUBBLE_STRETCH_X = 0.42;
const ACTIVE_BUBBLE_STRETCH_Y = 0.144;

const ACTIVE_BUBBLE_TINT_COLOR = 'rgba(0, 71, 60, 0.1)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linkGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: LINK_GROUP_GAP,
    padding: LINK_GROUP_PADDING,
    borderRadius: theme.radius['3xLarge'],
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  linkGroupSolid: {
    backgroundColor: theme.colors.WHITE,
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    pointerEvents: 'none',
  },
  activeBubble: {
    position: 'absolute',
    top: LINK_GROUP_PADDING,
    bottom: LINK_GROUP_PADDING,
    left: LINK_GROUP_PADDING,
    borderRadius: 1000,
    pointerEvents: 'none',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  activeBubbleItemCount?: number;
  activeTabIndexSV?: SharedValue<number>;
  glassFillOpacity?: number;
  isLiquidGlassEnabled?: boolean;
  layout?: AnimatedProps<ViewProps>['layout'];
  style?: ViewProps['style'];
}>;
